import React from "react";
import { Workflow, Clock, CheckCircle2, AlertTriangle, ExternalLink, Send, ArrowLeftCircle, FileUp, Truck, FileText, Paperclip, Calculator, ChevronDown, ChevronRight, X, MoreHorizontal, Link2, Pencil } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { Badge } from "../components/Badges";
import { JobDetailModal } from "../components/JobDetailModal";
import { MarkSentModal, MarkReceivedModal, UploadCertModal, ReadyDispatchModal, CostingModal, EditSubconLinkModal, EditSubconDetailsModal, deliveryLabel } from "../components/SubcontractorModals";
import { useFetch } from "../lib/useApi";
import { fmtDate, money } from "../lib/format";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

async function openPdfBlob(path) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`).getTime();
  if (Number.isNaN(d)) return null;
  return Math.floor((d - Date.now()) / 86400000);
}
function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`).getTime();
  if (Number.isNaN(d)) return null;
  return Math.floor((Date.now() - d) / 86400000);
}

const STAGE_META = {
  awaiting: { label: "To send", cls: "b-amber" },
  away: { label: "At subcontractor", cls: "b-violet" },
  returned: { label: "Returned", cls: "b-green" },
};

const REASON_META = {
  "SANAS": { cls: "b-blue", short: "SANAS" },
  "No In-House Capability": { cls: "b-amber", short: "No in-house" },
  "Repair": { cls: "b-violet", short: "Repair" },
};
function ReasonBadge({ reason }) {
  if (!reason) return <span className="t-mut">—</span>;
  const meta = REASON_META[reason] || { cls: "b-slate", short: reason };
  return <span title={reason}><Badge label={meta.short} cls={meta.cls} /></span>;
}

/**
 * Compact per-row actions: one primary "change status" button (stage-aware)
 * plus a "⋯ More" dropdown for the view/update actions (Print DN, Cert, Cost,
 * Open job). The menu is rendered with position:fixed (computed from the
 * trigger's rect) so it is never clipped by the collapsible group cards.
 */
function RowActions({ row, primary, items }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const btnRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);
  const toggle = (e) => {
    e.stopPropagation();
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 192) });
    setOpen((o) => !o);
  };
  const visItems = items.filter((it) => !it.hidden);
  return (
    <div style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
      {primary && (
        <button className="btn gold sm" onClick={primary.onClick} data-testid={primary.testid}>
          <primary.icon size={12} /> {primary.label}
        </button>
      )}
      <button ref={btnRef} className="btn ghost sm" onClick={toggle}
        data-testid={`subcon-actions-${row.id}`} title="More actions"
        aria-haspopup="true" aria-expanded={open}>
        <MoreHorizontal size={15} />
      </button>
      {open && pos && (
        <div className="row-menu" style={{ position: "fixed", top: pos.top, left: pos.left,
          zIndex: 1000, minWidth: 188 }} onClick={(e) => e.stopPropagation()}
          data-testid={`subcon-actions-menu-${row.id}`}>
          {visItems.map((it) => (
            <button key={it.label} className="row-menu-item" data-testid={it.testid}
              onClick={() => { setOpen(false); it.onClick(); }}>
              <it.icon size={13} /> {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Subcontractor() {
  const { can } = useAuth();
  const canEdit = can("subcontractor", "edit") || can("job_flow", "edit");
  const canCost = can("subcontractor_costing", "view");
  const { data, refetch } = useFetch("/subcontractor-jobs");
  const list = data || [];
  const [openJobId, setOpenJobId] = React.useState(null);
  const [selected, setSelected] = React.useState(new Set());
  const [collapsed, setCollapsed] = React.useState(new Set());
  const [printing, setPrinting] = React.useState(false);
  const [tab, setTab] = React.useState("all");
  const [reasonFilter, setReasonFilter] = React.useState("all");
  const [modal, setModal] = React.useState(null); // {kind, rows}

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const clearSel = () => setSelected(new Set());

  const printDn = async (ids) => {
    if (!ids.length) return;
    setPrinting(true);
    try { await openPdfBlob(`/subcontractor-dn.pdf?item_ids=${ids.join(",")}`); }
    catch (e) {
      const msg = apiErr(e);
      toast.error(msg.includes("different subcontractors")
        ? "Selected items go to different subcontractors — select items for one subcontractor at a time."
        : msg);
    } finally { setPrinting(false); }
  };

  const isOverdue = (row) => {
    if (row.stage !== "away") return false;
    const d = daysUntil(row.expected_return_date);
    return d != null && d < 0;
  };

  const awaiting = list.filter((x) => x.stage === "awaiting");
  const away = list.filter((x) => x.stage === "away");
  const overdue = away.filter(isOverdue);
  const returned = list.filter((x) => x.stage === "returned");

  // Filtered + ordered rows
  const tabbed = tab === "all" ? list
    : tab === "overdue" ? overdue
    : list.filter((x) => x.stage === tab);
  const reasonFiltered = reasonFilter === "all" ? tabbed
    : reasonFilter === "none" ? tabbed.filter((x) => !x.reason_for_subcontracting)
    : tabbed.filter((x) => x.reason_for_subcontracting === reasonFilter);
  const ordered = [...reasonFiltered].sort((a, b) => {
    if (isOverdue(b) - isOverdue(a)) return isOverdue(b) - isOverdue(a);
    const ord = { away: 0, awaiting: 1, returned: 2 };
    return (ord[a.stage] ?? 9) - (ord[b.stage] ?? 9);
  });

  // Group by Delivery Note (preserve first-appearance order)
  const groups = React.useMemo(() => {
    const m = new Map();
    for (const r of ordered) {
      const dn = r.delivery_note_number || "—";
      if (!m.has(dn)) m.set(dn, []);
      m.get(dn).push(r);
    }
    return [...m.entries()];
  }, [ordered]);

  const reasonCounts = list.reduce((acc, x) => {
    const k = x.reason_for_subcontracting || "none";
    acc[k] = (acc[k] || 0) + 1; return acc;
  }, {});

  // Selection helpers
  const selectedRows = list.filter((r) => selected.has(r.id));
  const selStages = new Set(selectedRows.map((r) => r.stage));
  const commonStage = selStages.size === 1 ? [...selStages][0] : null;
  const sameSubcontractor = new Set(selectedRows.map((r) => r.subcontractor_name || "")).size <= 1;

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(groups.map(([dn]) => dn)));

  const Tab = ({ id, label, count, danger }) => (
    <button className={`ftab ${tab === id ? "active" : ""}`} onClick={() => setTab(id)} data-testid={`subcon-tab-${id}`}>
      {label} <span className="c" style={danger && count ? { color: "#e0746f" } : {}}>{count}</span>
    </button>
  );

  const closeModal = () => setModal(null);
  const afterAction = () => { closeModal(); clearSel(); refetch?.(); };
  const openModal = (kind, rows) => setModal({ kind, rows });

  const Row = ({ row }) => {
    const away_ = row.stage === "away";
    const dRet = daysUntil(row.expected_return_date);
    const overdueRow = isOverdue(row);
    const daysAway = away_ ? daysSince(row.sent_date) : null;
    return (
      <tr data-testid={`subcon-row-${row.id}`} className={selected.has(row.id) ? "row-sel" : ""}>
        <td style={{ width: 32 }}>
          <input type="checkbox" checked={selected.has(row.id)}
            onChange={() => toggleSel(row.id)} data-testid={`subcon-select-${row.id}`} />
        </td>
        <td>
          <div className="t-strong">{row.description || "—"}</div>
          <div className="t-mut" style={{ fontSize: 11.5 }}>
            {row.customer_name || ""}{row.serial_number ? ` · SN ${row.serial_number}` : ""}
          </div>
        </td>
        <td className="t-strong">{row.subcontractor_name || "—"}</td>
        <td><ReasonBadge reason={row.reason_for_subcontracting} /></td>
        <td className="mono">{row.subcontractor_reference || "—"}</td>
        <td>{deliveryLabel(row.delivery_method)}</td>
        <td className="mono">{fmtDate(row.sent_date)}</td>
        <td className="mono">
          {away_ ? (
            row.expected_return_date ? (
              <span style={{ color: overdueRow ? "#a32424" : "inherit", fontWeight: overdueRow ? 700 : 400 }}>
                {fmtDate(row.expected_return_date)}
                {dRet != null && (
                  <span style={{ fontSize: 10.5, marginLeft: 6, color: overdueRow ? "#a32424" : "var(--text-3)" }}>
                    ({dRet === 0 ? "today" : dRet > 0 ? `in ${dRet}d` : `${-dRet}d overdue`})
                  </span>
                )}
              </span>
            ) : (daysAway != null ? <span className="t-mut">{daysAway}d away</span> : "—")
          ) : row.stage === "returned" ? (
            <span className="t-mut">back {fmtDate(row.returned_date)}</span>
          ) : "—"}
        </td>
        <td>
          <Badge label={overdueRow ? "Overdue" : STAGE_META[row.stage]?.label || row.stage}
            cls={overdueRow ? "b-red" : STAGE_META[row.stage]?.cls || "b-violet"} />
          {row.certificate_number && (
            <div style={{ fontSize: 10.5, marginTop: 3 }} className="t-mut" title="PTE certificate">
              <FileText size={10} style={{ verticalAlign: -1 }} /> PTE {row.certificate_number}
            </div>
          )}
          {row.subcontractor_certificate_number && (
            <div style={{ fontSize: 10.5, marginTop: 3 }} className="t-mut" title="Subcontractor certificate recorded">
              <CheckCircle2 size={10} style={{ verticalAlign: -1 }} /> Subcon {row.subcontractor_certificate_number}
            </div>
          )}
          {canCost && row.costing?.final_quote != null && (
            <div style={{ fontSize: 10.5, marginTop: 3, color: "#8a5a00" }} title="Suggested PTE quote (internal)">
              <Calculator size={10} style={{ verticalAlign: -1 }} /> {money(row.costing.final_quote)}
            </div>
          )}
          {row.attachments_count > 0 && (
            <div style={{ fontSize: 10.5 }} className="t-mut">
              <Paperclip size={10} style={{ verticalAlign: -1 }} /> {row.attachments_count} file{row.attachments_count > 1 ? "s" : ""}
            </div>
          )}
        </td>
        <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
          <RowActions row={row}
            primary={
              canEdit && row.stage === "awaiting"
                ? { label: "Send to Subcon", icon: Send, onClick: () => openModal("sent", [row]), testid: `subcon-marksent-${row.id}` }
              : canEdit && row.stage === "away"
                ? { label: "Receive", icon: ArrowLeftCircle, onClick: () => openModal("received", [row]), testid: `subcon-received-${row.id}` }
              : canEdit && row.stage === "returned"
                ? { label: "Dispatch", icon: Truck, onClick: () => openModal("dispatch", [row]), testid: `subcon-readydispatch-${row.id}` }
              : null
            }
            items={[
              { label: "Print delivery note", icon: FileText, onClick: () => printDn([row.id]), testid: `subcon-dn-${row.id}` },
              { label: "Link to Subcon", icon: Link2, onClick: () => openModal("link", [row]), testid: `subcon-editlink-${row.id}`, hidden: !canEdit },
              { label: "Upload certificate", icon: FileUp, onClick: () => openModal("cert", [row]), testid: `subcon-uploadcert-${row.id}`, hidden: !canEdit },
              { label: "Costing (internal)", icon: Calculator, onClick: () => openModal("costing", [row]), testid: `subcon-costing-${row.id}`, hidden: !canCost },
              { label: "Open job", icon: ExternalLink, onClick: () => setOpenJobId(row.job_id), testid: `subcon-open-${row.id}` },
            ]}
          />
        </td>
      </tr>
    );
  };

  const GroupCard = ({ dn, rows }) => {
    const open = !collapsed.has(dn);
    const ids = rows.map((r) => r.id);
    const allSel = ids.every((id) => selected.has(id));
    const someSel = ids.some((id) => selected.has(id));
    const customers = [...new Set(rows.map((r) => r.customer_name).filter(Boolean))];
    const subs = [...new Set(rows.map((r) => r.subcontractor_name).filter(Boolean))];
    const counts = rows.reduce((a, r) => { a[r.stage] = (a[r.stage] || 0) + 1; return a; }, {});
    const overdueCount = rows.filter(isOverdue).length;
    const toggleGroup = () => setCollapsed((s) => {
      const n = new Set(s); n.has(dn) ? n.delete(dn) : n.add(dn); return n;
    });
    const toggleGroupSel = (checked) => setSelected((s) => {
      const n = new Set(s); ids.forEach((id) => (checked ? n.add(id) : n.delete(id))); return n;
    });

    return (
      <div className="card" style={{ marginBottom: 10, overflow: "hidden" }} data-testid={`subcon-group-${dn}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
          background: someSel ? "#fff8ec" : "var(--card-2)", borderBottom: open ? "1px solid var(--line)" : "none" }}>
          <input type="checkbox" checked={allSel} ref={(el) => { if (el) el.indeterminate = someSel && !allSel; }}
            onChange={(e) => toggleGroupSel(e.target.checked)} data-testid={`subcon-group-select-${dn}`}
            title="Select all items on this delivery note" />
          <button type="button" onClick={toggleGroup} data-testid={`subcon-group-toggle-${dn}`}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none",
              cursor: "pointer", flex: 1, minWidth: 0, textAlign: "left", padding: 0 }}>
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <FileText size={14} color="var(--gold-deep)" />
            <span className="t-strong mono" style={{ fontSize: 14, whiteSpace: "nowrap" }}>{dn}</span>
            <span className="t-mut" style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {customers.join(", ")}{subs.length ? ` · ${subs.join(", ")}` : ""}
            </span>
          </button>
          <span style={{ display: "inline-flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
            {counts.awaiting > 0 && <Badge label={`${counts.awaiting} to send`} cls="b-amber" />}
            {counts.away > 0 && <Badge label={`${counts.away} away`} cls="b-violet" />}
            {counts.returned > 0 && <Badge label={`${counts.returned} returned`} cls="b-green" />}
            {overdueCount > 0 && <Badge label={`${overdueCount} overdue`} cls="b-red" />}
            <span className="t-mut" style={{ fontSize: 12 }}>{rows.length} item{rows.length > 1 ? "s" : ""}</span>
          </span>
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexShrink: 0, marginLeft: 8 }}
            onClick={(e) => e.stopPropagation()}>
            <button className="btn ghost sm" onClick={() => printDn(ids)} disabled={printing}
              data-testid={`subcon-group-print-${dn}`} title="Print the delivery note for every item on this subcontractor job">
              <FileText size={12} /> {printing ? "…" : "Print DN"}
            </button>
            {canEdit && (
              <button className="btn ghost sm" onClick={() => openModal("link", rows)}
                data-testid={`subcon-group-assign-${dn}`} title="Assign one correct subcontractor to every item on this job">
                <Link2 size={12} /> Assign Subcon
              </button>
            )}
            {canEdit && (
              <button className="btn ghost sm" onClick={() => openModal("editdetails", rows)}
                data-testid={`subcon-group-editdetails-${dn}`} title="Edit reference / delivery method / expected return for every item on this job">
                <Pencil size={12} /> Edit Details
              </button>
            )}
          </span>
        </div>
        {open && (
          <table className="dt" data-testid={`subcon-table-${dn}`}>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Equipment</th>
                <th>Subcontractor</th>
                <th>Reason</th>
                <th>Reference</th>
                <th>Delivery</th>
                <th>Sent</th>
                <th>Expected back</th>
                <th>Status</th>
                <th style={{ width: 150 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => <Row key={row.id} row={row} />)}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <>
      <PageHead crumb="Workflow" title="Subcontractor"
        sub="Items sent out for external calibration — grouped by delivery note. Select multiple items to apply an action to all at once." />
      <div className="grid cols-4" style={{ marginBottom: 18 }}>
        <div className="card pad stat" data-testid="subcon-stat-awaiting"><div className="lab"><span className="ico b-amber"><Send size={16} /></span>To send</div><div className="num">{awaiting.length}</div></div>
        <div className="card pad stat" data-testid="subcon-stat-out"><div className="lab"><span className="ico b-violet"><Workflow size={16} /></span>At subcontractor</div><div className="num">{away.length}</div></div>
        <div className="card pad stat" data-testid="subcon-stat-overdue"><div className="lab"><span className="ico b-red"><AlertTriangle size={16} /></span>Overdue return</div><div className="num" style={{ color: overdue.length ? "#a32424" : "inherit" }}>{overdue.length}</div></div>
        <div className="card pad stat" data-testid="subcon-stat-returned"><div className="lab"><span className="ico b-green"><CheckCircle2 size={16} /></span>Recently returned</div><div className="num">{returned.length}</div></div>
      </div>

      <div className="card" style={{ overflow: "visible" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
          borderBottom: "1px solid var(--line)", flexWrap: "wrap" }}>
          <Tab id="all" label="All" count={list.length} />
          <Tab id="awaiting" label="To send" count={awaiting.length} />
          <Tab id="away" label="At subcontractor" count={away.length} />
          <Tab id="overdue" label="Overdue" count={overdue.length} danger />
          <Tab id="returned" label="Returned" count={returned.length} />
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={expandAll} data-testid="subcon-expand-all">Expand all</button>
          <button className="btn ghost sm" onClick={collapseAll} data-testid="subcon-collapse-all">Collapse all</button>
          <select className="input" value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}
            data-testid="subcon-reason-filter"
            style={{ width: "auto", padding: "5px 26px 5px 10px", fontSize: 12.5, height: 30 }}
            title="Filter by Sub Con reason">
            <option value="all">All reasons ({list.length})</option>
            <option value="No In-House Capability">No In-House Capability ({reasonCounts["No In-House Capability"] || 0})</option>
            <option value="SANAS">SANAS ({reasonCounts["SANAS"] || 0})</option>
            <option value="Repair">Repair ({reasonCounts["Repair"] || 0})</option>
            <option value="none">No reason set ({reasonCounts["none"] || 0})</option>
          </select>
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div data-testid="subcon-bulk-bar" style={{ display: "flex", alignItems: "center", gap: 8,
            padding: "10px 12px", background: "#fff8ec", borderBottom: "1px solid var(--line)", flexWrap: "wrap",
            position: "sticky", top: 0, zIndex: 5 }}>
            <span className="t-strong" style={{ fontSize: 13 }} data-testid="subcon-bulk-count">{selected.size} selected</span>
            <span className="t-mut" style={{ fontSize: 12 }}>
              {commonStage ? `· ${STAGE_META[commonStage]?.label}` : "· mixed stages"}
            </span>
            <span style={{ flex: 1 }} />
            <button className="btn ghost sm" onClick={() => printDn([...selected])}
              disabled={printing || !sameSubcontractor} data-testid="subcon-bulk-dn"
              title={sameSubcontractor ? "Print a combined delivery note" : "Items go to different subcontractors"}>
              <FileText size={13} /> {printing ? "Preparing…" : "Print DN"}
            </button>
            {canEdit && commonStage === "awaiting" && (
              <button className="btn gold sm" onClick={() => openModal("sent", selectedRows)} data-testid="subcon-bulk-marksent">
                <Send size={13} /> Send to Subcon ({selected.size})
              </button>
            )}
            {canEdit && commonStage === "away" && (
              <button className="btn gold sm" onClick={() => openModal("received", selectedRows)} data-testid="subcon-bulk-received">
                <ArrowLeftCircle size={13} /> Received Back ({selected.size})
              </button>
            )}
            {canEdit && commonStage === "returned" && (
              <button className="btn gold sm" onClick={() => openModal("dispatch", selectedRows)} data-testid="subcon-bulk-dispatch">
                <Truck size={13} /> In Dispatch ({selected.size})
              </button>
            )}
            {canEdit && (
              <button className="btn ghost sm" onClick={() => openModal("cert", selectedRows)} data-testid="subcon-bulk-cert">
                <FileUp size={13} /> Upload Cert ({selected.size})
              </button>
            )}
            <button className="btn ghost sm" onClick={clearSel} data-testid="subcon-bulk-clear" title="Clear selection">
              <X size={13} /> Clear
            </button>
          </div>
        )}

        <div style={{ padding: 12 }}>
          {groups.map(([dn, rows]) => <GroupCard key={dn} dn={dn} rows={rows} />)}
          {!groups.length && (
            <div style={{ textAlign: "center", padding: 30, color: "var(--text-3)" }}>
              No subcontractor items here — populated automatically when an item is sent to a subcontractor
              (Job Flow Board / Receiving).
              <span style={{ display: "block", fontSize: 11, marginTop: 6 }}>
                <Clock size={11} style={{ verticalAlign: -1 }} /> "Expected back" drives the overdue warning.
              </span>
            </div>
          )}
        </div>
      </div>

      {modal?.kind === "sent" && <MarkSentModal rows={modal.rows} onClose={closeModal} onDone={afterAction} />}
      {modal?.kind === "received" && <MarkReceivedModal rows={modal.rows} onClose={closeModal} onDone={afterAction} />}
      {modal?.kind === "cert" && <UploadCertModal rows={modal.rows} onClose={closeModal} onDone={afterAction} />}
      {modal?.kind === "dispatch" && <ReadyDispatchModal rows={modal.rows} onClose={closeModal} onDone={afterAction} />}
      {modal?.kind === "costing" && <CostingModal rows={modal.rows} onClose={closeModal} onDone={afterAction} />}
      {modal?.kind === "link" && <EditSubconLinkModal rows={modal.rows} onClose={closeModal} onDone={afterAction} />}
      {modal?.kind === "editdetails" && <EditSubconDetailsModal rows={modal.rows} onClose={closeModal} onDone={afterAction} />}
      {openJobId && <JobDetailModal jobId={openJobId} onClose={() => setOpenJobId(null)} onChanged={refetch} />}
    </>
  );
}
